import type {
  GetVerificationResponse,
  IdDocumentType,
  IdImageSide,
  VerificationListItem,
  VerificationStatus,
} from "@wallyo/contracts/console/verifications";
import { CATALOGS, t, type MessageId } from "../../i18n";
import { formatDate } from "../../i18n/format";
import type { Tone } from "../customers/customer-format";

export type VerificationDetail = GetVerificationResponse["verification"];

export const STATUS_PILL: Record<VerificationStatus, { label: MessageId; tone: Tone }> = {
  submitted: { label: "verifications.status.submitted", tone: "info" },
  flagged: { label: "verifications.status.flagged", tone: "bad" },
  approved: { label: "verifications.status.approved", tone: "ok" },
  rejected: { label: "verifications.status.rejected", tone: "mute" },
};

/** Still waiting for a reviewer: the only states a decision can be taken on. */
export const isOpen = (status: VerificationStatus): boolean => status === "submitted" || status === "flagged";

export const documentTypeName = (type: IdDocumentType): string => t(`verifications.document.${type}` as const);

export const sideName = (side: IdImageSide): string => t(`verifications.side.${side}` as const);

/** A trade in words; a trade newer than this build shows as the server sent it. */
export function tradeOf(category: string): string {
  const id = `trade.${category}`;
  return Object.hasOwn(CATALOGS.fr, id) ? t(id as MessageId) : category;
}

/** "•••• 4471": only the last 4 characters ever reach the console. */
export const maskedNumber = (last4: string): string => `•••• ${last4}`;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How long it has waited: "12 min", "5 h", "2 j". Two days or more is a
 * warning, as in the prototype.
 */
export function ageOf(submittedAt: string, now: Date = new Date()): { text: string; tone: Tone } {
  const waited = Math.max(0, now.getTime() - new Date(submittedAt).getTime());
  if (waited < HOUR) return { text: t("unit.minutes", { value: Math.floor(waited / MINUTE) }), tone: "mute" };
  if (waited < DAY) return { text: t("unit.hours", { value: Math.floor(waited / HOUR) }), tone: "mute" };
  const days = Math.floor(waited / DAY);
  return { text: t("unit.days", { value: days }), tone: days >= 2 ? "warn" : "mute" };
}

/** A calendar day from the document ("1991-03-14"), read in UTC so it never shifts a day. */
export function formatDocumentDay(day: string): string {
  return formatDate(new Date(`${day.slice(0, 10)}T00:00:00Z`), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "CAMARA Sékou" and "Sékou Camara" are the same name: case, accents and order don't count. */
export function sameName(a: string, b: string): boolean {
  const words = (name: string) =>
    name
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .toLowerCase()
      .split(/[^\p{L}\p{N}]+/u)
      .filter(Boolean)
      .sort()
      .join(" ");
  return words(a) === words(b);
}

export type MatchPill = { label: MessageId; tone: Tone };

export type ComparisonRow = {
  id: "name" | "dateOfBirth" | "expiresOn" | "documentNumber";
  field: MessageId;
  /** What the check read on the document, or null when nothing has read it (manual review). */
  read: string | null;
  /** What the pro typed, or null when the app doesn't ask for it. */
  typed: string | null;
  pill: MatchPill;
  mono?: boolean;
};

const TO_CHECK: MatchPill = { label: "verifications.match.check", tone: "mute" };

/** The document next to what the pro typed, each row with its pill. */
export function comparisonRows(detail: VerificationDetail, now: Date = new Date()): ComparisonRow[] {
  const { extracted } = detail.check;
  const today = now.toISOString().slice(0, 10);
  return [
    {
      id: "name",
      field: "verifications.field.name",
      read: extracted.fullName,
      typed: detail.pro.name,
      pill:
        extracted.fullName === null
          ? TO_CHECK
          : sameName(extracted.fullName, detail.pro.name)
            ? { label: "verifications.match.same", tone: "ok" }
            : { label: "verifications.match.different", tone: "bad" },
    },
    {
      id: "dateOfBirth",
      field: "verifications.field.dateOfBirth",
      read: extracted.dateOfBirth ? formatDocumentDay(extracted.dateOfBirth) : null,
      typed: null,
      pill: extracted.dateOfBirth ? { label: "verifications.match.readOnly", tone: "info" } : TO_CHECK,
    },
    {
      id: "expiresOn",
      field: "verifications.field.expiresOn",
      read: extracted.expiresOn ? formatDocumentDay(extracted.expiresOn) : null,
      typed: null,
      pill:
        extracted.expiresOn === null
          ? TO_CHECK
          : extracted.expiresOn.slice(0, 10) < today
            ? { label: "verifications.match.expired", tone: "bad" }
            : { label: "verifications.match.valid", tone: "ok" },
    },
    {
      id: "documentNumber",
      field: "verifications.field.documentNumber",
      read: null,
      typed: maskedNumber(detail.documentNumberLast4),
      pill: detail.matchesBannedDocument
        ? { label: "verifications.match.banned", tone: "bad" }
        : { label: "verifications.match.notBanned", tone: "ok" },
      mono: true,
    },
  ];
}

/** "Électricien · Matoto · Carte d'identité": a queue row's second line. */
export function describeRow(item: VerificationListItem): string {
  return [tradeOf(item.pro.category), item.pro.city, documentTypeName(item.documentType)].join(" · ");
}
